<script setup lang="ts">
import { computed } from 'vue'
import Dropdown from './Dropdown.vue'
import type { DropdownOption } from './Dropdown.vue'
import { track } from '@/lib/usageTracker'
import type { Integration } from '@/stores/authStore'

export interface IntegrationMeta {
  name: string
  iconColor: string
}

const props = withDefaults(defineProps<{
  items: Record<string, Integration>
  metas?: Record<string, IntegrationMeta>
  modelValue?: string
  label?: string
  triggerLabel?: string
  emptyMessage?: string
}>(), {
  label: 'Active integration',
  triggerLabel: 'No provider selected',
  emptyMessage: 'No integrations connected yet.',
})

const emit = defineEmits<{
  select: [key: string]
}>()

const PROVIDER_META: Record<string, IntegrationMeta> = {
  QUICKBOOKS: { name: 'QuickBooks', iconColor: '#2ca01c' },
  XERO: { name: 'Xero', iconColor: '#13B5EA' },
}

const activeKey = computed(() => props.modelValue || Object.keys(props.items)[0] || '')

const activeMeta = computed((): IntegrationMeta | null => {
  const key = activeKey.value
  if (!key) return null
  return { ...PROVIDER_META[key], ...props.metas?.[key] }
})

const activeIntegration = computed(() => props.items[activeKey.value])
const isConnected = computed(() => activeIntegration.value?.status === 'authenticated')

const options = computed<DropdownOption[]>(() =>
  Object.entries(props.items).map(([key, integration]) => {
    const meta: IntegrationMeta = { ...PROVIDER_META[key], ...props.metas?.[key] }
    return {
      key,
      label: meta.name || key,
      iconColor: meta.iconColor || '#6b7280',
      badge: integration.status === 'authenticated' ? 'Connected' : 'Disconnected',
      badgeVariant: integration.status === 'authenticated' ? 'connected' as const : 'disconnected' as const,
      meta: integration.status === 'authenticated' ? 'Ready to sync' : 'Needs attention',
    }
  })
)

function handleSelect(key: string) {
  track('btn:SwitchIntegration', 'click', { provider: key })
  emit('select', key)
}
</script>

<template>
  <div class="integration-dropdown">
    <Dropdown
      :options="options"
      :model-value="activeKey"
      :label="label"
      :trigger-label="activeMeta?.name ?? triggerLabel"
      :trigger-meta="isConnected ? 'Authenticated and ready' : 'Needs attention'"
      :trigger-badge="isConnected ? 'Connected' : 'Disconnected'"
      :trigger-badge-variant="isConnected ? 'connected' : 'disconnected'"
      :trigger-icon-color="activeMeta?.iconColor"
      :empty-message="emptyMessage"
      @select="handleSelect"
    >
      <template #item="{ option }">
        <span class="item-dot" :style="{ backgroundColor: option.iconColor }" />
        <span class="item-copy">
          <span class="item-label">{{ option.label }}</span>
          <span class="item-meta">{{ option.meta }}</span>
        </span>
        <span
          class="item-badge"
          :class="`badge-${option.badgeVariant}`"
        >
          {{ option.badge }}
        </span>
      </template>
    </Dropdown>
  </div>
</template>

<style scoped>
.integration-dropdown {
  padding: 0.7rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-soft);
  background: rgba(246, 244, 238, 0.9);
}

.item-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
}

.item-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.item-label {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--color-ink);
}

.item-meta {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

.item-badge {
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-sm);
  padding: 0.18rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 600;
}

.badge-connected {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success);
}

.badge-disconnected {
  background: rgba(239, 68, 68, 0.09);
  color: var(--color-error);
}
</style>
